/** Planned lump-sum super withdrawals (on top of strategy income). Max 10 per scenario. */

export const MAX_LUMP_SUM_WITHDRAWALS = 10;

const emptyRow = () => ({ scheduleType: 'year', scheduleValue: '', amount: '', label: '' });

export const defaultLumpSumWithdrawalRows = () =>
  Array.from({ length: MAX_LUMP_SUM_WITHDRAWALS }, emptyRow);

/** Always 10 UI rows; merge saved/partial arrays safely. */
export function normalizeLumpSumWithdrawalRows(raw) {
  const defaults = defaultLumpSumWithdrawalRows();
  if (!Array.isArray(raw)) return defaults;
  return defaults.map((empty, idx) => {
    const row = raw[idx];
    if (!row || typeof row !== 'object') return { ...empty };
    return {
      scheduleType: row.scheduleType === 'age' ? 'age' : 'year',
      scheduleValue: row.scheduleValue ?? '',
      amount: row.amount ?? '',
      label: row.label ?? '',
    };
  });
}

/**
 * Normalise UI / saved state into [{ scheduleType: 'age'|'year', scheduleValue, amount, label }].
 */
export function parseLumpSumWithdrawals(inputs) {
  if (!inputs) return [];

  if (Array.isArray(inputs.parsedLumpSumWithdrawals)) {
    return inputs.parsedLumpSumWithdrawals
      .map((row) => ({
        scheduleType: row.scheduleType === 'year' ? 'year' : 'age',
        scheduleValue: Number(row.scheduleValue),
        amount: Number(row.amount) || 0,
        label: String(row.label || '').trim(),
      }))
      .filter((row) => row.amount > 0 && Number.isFinite(row.scheduleValue) && row.scheduleValue > 0)
      .slice(0, MAX_LUMP_SUM_WITHDRAWALS);
  }

  if (Array.isArray(inputs.lumpSumWithdrawals)) {
    return inputs.lumpSumWithdrawals
      .map((row) => ({
        scheduleType: row.scheduleType === 'year' ? 'year' : 'age',
        scheduleValue: Number(row.scheduleValue),
        amount: Number(row.amount) || 0,
        label: String(row.label || '').trim(),
      }))
      .filter((row) => row.amount > 0 && Number.isFinite(row.scheduleValue) && row.scheduleValue > 0)
      .slice(0, MAX_LUMP_SUM_WITHDRAWALS);
  }

  const out = [];
  for (let n = 1; n <= MAX_LUMP_SUM_WITHDRAWALS; n++) {
    const amount = Number(inputs[`lumpSum_amount${n}`]) || 0;
    if (amount <= 0) continue;
    const age = inputs[`lumpSum_age${n}`];
    const year = inputs[`lumpSum_year${n}`];
    if (year !== '' && year != null && Number.isFinite(Number(year))) {
      out.push({
        scheduleType: 'year',
        scheduleValue: Number(year),
        amount,
        label: String(inputs[`lumpSum_label${n}`] || '').trim(),
      });
    } else if (age !== '' && age != null && Number.isFinite(Number(age))) {
      out.push({
        scheduleType: 'age',
        scheduleValue: Number(age),
        amount,
        label: String(inputs[`lumpSum_label${n}`] || '').trim(),
      });
    }
  }
  return out;
}

/** First year (0-based) in which you or your partner reach `age`; null if both are already past it. */
const firstYearIndexReachingAge = (age, yourAge, partnerAge) => {
  const candidates = [age - Number(yourAge), age - Number(partnerAge ?? yourAge)]
    .filter((idx) => Number.isFinite(idx) && idx >= 0);
  return candidates.length ? Math.min(...candidates) : null;
};

/**
 * Lump sum due in simulation year `yearIndex` (0-based).
 * Age rows fire once, in the first year you OR your partner reach that age.
 * Plan-year rows use 1-based year of the projection (year 1 = first simulated year).
 */
export function getLumpSumAmountForYear(lumpSums, { yearIndex, yourAge, partnerAge }) {
  if (!lumpSums?.length) return 0;
  const planYear = yearIndex + 1;
  let total = 0;
  for (const row of lumpSums) {
    const amt = Number(row.amount) || 0;
    if (amt <= 0) continue;
    if (row.scheduleType === 'year') {
      if (Number(row.scheduleValue) === planYear) total += amt;
    } else if (firstYearIndexReachingAge(Number(row.scheduleValue), yourAge, partnerAge) === yearIndex) {
      total += amt;
    }
  }
  return total;
}

export function hasLumpSumWithdrawals(inputs) {
  return parseLumpSumWithdrawals(inputs).length > 0;
}

export function isLumpSumRowTouched(row) {
  return String(row?.scheduleValue ?? '').trim() !== ''
    || String(row?.amount ?? '').trim() !== ''
    || String(row?.label ?? '').trim() !== '';
}

/** Show existing rows plus one spare blank row, instead of all 10 at once. */
export function getVisibleLumpSumRows(rawRows) {
  const rows = normalizeLumpSumWithdrawalRows(rawRows);
  const lastTouchedIndex = rows.reduce((last, row, idx) => (isLumpSumRowTouched(row) ? idx : last), -1);
  const visibleCount = Math.min(MAX_LUMP_SUM_WITHDRAWALS, Math.max(1, lastTouchedIndex + 2));
  return rows.slice(0, visibleCount).map((row, idx) => ({ row, idx }));
}

/**
 * Users often type "4" to mean plan year 4. If a row is still marked as age
 * but the value is below both current ages and within the projection length,
 * treat it as a plan-year row so the withdrawal fires instead of becoming inert.
 */
export function coerceLumpSumRowsForProjection(rawRows, { yourAge, partnerAge, periodLength }) {
  const rows = normalizeLumpSumWithdrawalRows(rawRows);
  const minCurrentAge = Math.min(Number(yourAge) || Infinity, Number(partnerAge ?? yourAge) || Infinity);
  const years = Math.max(0, Number(periodLength) || 0);
  return rows.map((row) => {
    const scheduleValue = Number(row.scheduleValue);
    const amount = Number(row.amount) || 0;
    if (
      row.scheduleType === 'age'
      && amount > 0
      && Number.isFinite(scheduleValue)
      && scheduleValue > 0
      && scheduleValue <= years
      && scheduleValue < minCurrentAge
    ) {
      return { ...row, scheduleType: 'year' };
    }
    return row;
  });
}

/** Which projection years (1-based) have a lump sum, and rows that never fire. */
export function getLumpSumScheduleDiagnostics(inputs, { yourAge, partnerAge, periodLength }) {
  const parsed = parseLumpSumWithdrawals({
    ...inputs,
    lumpSumWithdrawals: coerceLumpSumRowsForProjection(inputs?.lumpSumWithdrawals, { yourAge, partnerAge, periodLength }),
  });
  const years = Math.max(0, Number(periodLength) || 0);
  const ageYou = Number(yourAge) || 0;
  const agePartner = Number(partnerAge ?? yourAge) || ageYou;
  const firingYears = [];
  for (let i = 0; i < years; i++) {
    const amount = getLumpSumAmountForYear(parsed, { yearIndex: i, yourAge: ageYou, partnerAge: agePartner });
    if (amount > 0) firingYears.push({ planYear: i + 1, amount });
  }
  const inertRows = parsed.filter((row) => {
    for (let i = 0; i < years; i++) {
      const planYear = i + 1;
      const ageYouInYear = ageYou + i;
      const agePartnerInYear = agePartner + i;
      if (row.scheduleType === 'year') {
        if (Number(row.scheduleValue) === planYear) return false;
      } else if (Number(row.scheduleValue) === ageYouInYear || Number(row.scheduleValue) === agePartnerInYear) {
        return false;
      }
    }
    return true;
  });
  return { parsed, firingYears, inertRows };
}

export function formatLumpSumRow(row) {
  const amt = Math.round(Number(row.amount) || 0).toLocaleString();
  if (row.scheduleType === 'year') return `$${amt} in plan year ${row.scheduleValue}`;
  return `$${amt} at age ${row.scheduleValue}`;
}
